import React, { useEffect, useMemo, useRef, useState } from 'react';
import { getActiveDirectoryUsers, useUsersDirectory } from '../../lib/usersDirectory';

function displayName(user) {
  return user?.full_name?.trim() || user?.email || 'Unknown';
}

/**
 * Shared user picker.
 *
 * @param {'prop'|'directory'} [source='prop']
 *   - prop: use the `users` array (legacy / filtered lists).
 *   - directory: load agency-wide display roster via get_agency_users_directory
 *     (works for Managers under scoped users RLS). Optional `users` then acts as
 *     an allowlist of selectable ids when provided and non-empty.
 * @param {boolean} [activeOnly=true] When source=directory, filter is_active.
 */
export default function UserPickerDropdown({
  label,
  users: usersProp,
  value,
  onChange,
  placeholder = 'Select…',
  disabled = false,
  source = 'prop',
  activeOnly = true,
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const wrapperRef = useRef(null);
  const { users: directoryUsers, loading: directoryLoading } = useUsersDirectory();

  const users = useMemo(() => {
    if (source !== 'directory') return usersProp || [];

    let list = activeOnly
      ? getActiveDirectoryUsers(directoryUsers)
      : [...(directoryUsers || [])];

    // Explicit users array = allowlist (including empty). Omitted = full directory.
    if (Array.isArray(usersProp)) {
      const allow = new Set(usersProp.map((u) => u.id));
      list = list.filter((u) => allow.has(u.id));
      const propById = new Map(usersProp.map((u) => [u.id, u]));
      list = list.map((u) => {
        const extra = propById.get(u.id);
        return extra ? { ...u, ...extra, full_name: u.full_name || extra.full_name } : u;
      });
    }
    return list;
  }, [source, usersProp, directoryUsers, activeOnly]);

  const selected = useMemo(
    () => (value ? users.find((u) => u.id === value) : null),
    [users, value],
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return users;
    return users.filter((u) => {
      const name = displayName(u).toLowerCase();
      const email = (u.email || '').toLowerCase();
      return name.includes(q) || email.includes(q);
    });
  }, [users, search]);

  useEffect(() => {
    if (!open) return;
    const onOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', onOutside);
    return () => document.removeEventListener('mousedown', onOutside);
  }, [open]);

  return (
    <div ref={wrapperRef} className="relative">
      {label ? (
        <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      ) : null}
      <button
        type="button"
        disabled={disabled || (source === 'directory' && directoryLoading)}
        onClick={() => setOpen((o) => !o)}
        className="flex h-10 w-full items-center justify-between rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-left focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent disabled:opacity-50"
      >
        <span className={selected ? 'text-gray-900 truncate' : 'text-gray-400 truncate'}>
          {selected ? displayName(selected) : placeholder}
        </span>
        <span className="text-gray-400 text-xs shrink-0 ml-2">▾</span>
      </button>
      {open && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg max-h-56 overflow-hidden flex flex-col">
          <input
            type="text"
            className="w-full border-b border-gray-100 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-inset focus:ring-primary"
            placeholder="Search…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            autoFocus
          />
          <ul className="overflow-y-auto max-h-44">
            <li>
              <button
                type="button"
                className="w-full text-left px-3 py-2 text-sm text-gray-500 hover:bg-gray-50"
                onClick={() => {
                  onChange(null);
                  setOpen(false);
                  setSearch('');
                }}
              >
                None
              </button>
            </li>
            {filtered.map((u) => (
              <li key={u.id}>
                <button
                  type="button"
                  className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 ${
                    u.id === value ? 'text-primary font-medium bg-primary/5' : 'text-gray-800'
                  }`}
                  onClick={() => {
                    onChange(u.id);
                    setOpen(false);
                    setSearch('');
                  }}
                >
                  <span className="block truncate font-medium">{displayName(u)}</span>
                  {u.email ? <span className="block truncate text-xs text-gray-400">{u.email}</span> : null}
                </button>
              </li>
            ))}
            {filtered.length === 0 && (
              <li className="px-3 py-2 text-xs text-gray-400">No matches</li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
}
